import React, { useEffect, useState } from 'react';
import { getLandIntelligence } from '../services/landService';
import { getTenants } from '../services/tenantService';
import LandValueMap, { CATEGORY_COLOR, CATEGORY_LABEL, CATEGORY_ICON } from '../components/land/LandValueMap';

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ value, label, color = '#6366f1' }) {
  return (
    <div style={{
      background: '#1e293b', borderRadius: 8, padding: '14px 18px',
      borderTop: `3px solid ${color}`, flex: '1 1 120px', minWidth: 100,
    }}>
      <div style={{ fontSize: 26, fontWeight: 700, color: '#f1f5f9' }}>{value}</div>
      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{label}</div>
    </div>
  );
}

// ─── Horizontal bar ───────────────────────────────────────────────────────────
function DistBar({ label, count, max, color, icon }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12,
        color: '#94a3b8', marginBottom: 4 }}>
        <span>{icon && `${icon} `}{label}</span>
        <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{count}</span>
      </div>
      <div style={{ height: 6, background: '#0f172a', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3,
          transition: 'width 0.3s' }} />
      </div>
    </div>
  );
}

// ─── Recent value row ─────────────────────────────────────────────────────────
const STATUS_COLOR = { draft: '#94a3b8', verified: '#22c55e', disputed: '#f59e0b', archived: '#475569' };

function RecentRow({ v }) {
  const color = CATEGORY_COLOR[v.category] || '#94a3b8';
  const icon  = CATEGORY_ICON[v.category]  || '📍';
  return (
    <div style={{ display: 'flex', gap: 10, padding: '9px 0', borderBottom: '1px solid #1e293b',
      alignItems: 'flex-start' }}>
      <span style={{ fontSize: 18, flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 13,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {v.name}
        </div>
        <div style={{ fontSize: 11, color: '#64748b' }}>
          <span style={{ color }}>{CATEGORY_LABEL[v.category] || v.category}</span>
          {v.tenantId?.name && ` · ${v.tenantId.name}`}
        </div>
      </div>
      <span style={{ fontSize: 11, color: STATUS_COLOR[v.status] || '#94a3b8',
        flexShrink: 0, fontWeight: 600 }}>
        {v.status}
      </span>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function LandIntelligencePage() {
  const [data,     setData]     = useState(null);
  const [tenants,  setTenants]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [catFilter, setCatFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [activeTab, setActiveTab] = useState('map');  // 'map' | 'breakdown' | 'recent'

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (catFilter)    params.category = catFilter;
      if (tenantFilter) params.tenantId = tenantFilter;
      const [intRes, tenRes] = await Promise.all([
        getLandIntelligence(params),
        getTenants(),
      ]);
      setData(intRes.data);
      setTenants(tenRes.data);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [catFilter, tenantFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const maxCat =  data?.byCategory?.[0]?.count || 1;
  const maxTen =  data?.byTenant?.[0]?.count   || 1;
  const totalValues = data?.byStatus?.reduce((acc, s) => acc + s.count, 0) || 0;
  const verifiedCount = data?.byStatus?.find(s => s._id === 'verified')?.count || 0;
  const draftCount    = data?.byStatus?.find(s => s._id === 'draft')?.count    || 0;

  const selectStyle = {
    background: '#1e293b', border: '1px solid #334155', borderRadius: 6,
    color: '#e2e8f0', padding: '7px 12px', fontSize: 13, cursor: 'pointer',
  };

  const tabBtn = (key, label) => (
    <button onClick={() => setActiveTab(key)} style={{
      background: activeTab === key ? '#164e63' : '#1e293b',
      border: `1px solid ${activeTab === key ? '#0ea5e9' : '#334155'}`,
      color: activeTab === key ? '#7dd3fc' : '#94a3b8',
      borderRadius: 6, padding: '7px 16px', cursor: 'pointer', fontSize: 13,
      fontWeight: activeTab === key ? 700 : 400,
    }}>
      {label}
    </button>
  );

  return (
    <div style={{ padding: '24px 28px' }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ color: '#f1f5f9', margin: 0, fontSize: 22, fontWeight: 700 }}>
          🌐 Land Intelligence
        </h2>
        <p style={{ color: '#64748b', fontSize: 13, margin: '4px 0 0' }}>
          Cross-community map of culturally and ecologically significant land values.
        </p>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)} style={selectStyle}>
          <option value="">All categories</option>
          {Object.entries(CATEGORY_LABEL).map(([v, l]) => (
            <option key={v} value={v}>{CATEGORY_ICON[v]} {l}</option>
          ))}
        </select>
        {tenants.length > 1 && (
          <select value={tenantFilter} onChange={e => setTenantFilter(e.target.value)} style={selectStyle}>
            <option value="">All communities</option>
            {tenants.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
          </select>
        )}
      </div>

      {/* Stats */}
      {!loading && data && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
          <StatCard value={totalValues}   label="Total values"    color="#6366f1" />
          <StatCard value={verifiedCount} label="Verified"        color="#22c55e" />
          <StatCard value={draftCount}    label="Pending draft"   color="#94a3b8" />
          <StatCard value={data.byTenant?.length || 0} label="Communities" color="#0ea5e9" />
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {tabBtn('map',       '🗺️ Map')}
        {tabBtn('breakdown', '📊 Breakdown')}
        {tabBtn('recent',    '🕐 Recent')}
      </div>

      {loading ? (
        <div style={{ color: '#64748b', padding: '40px 0', textAlign: 'center' }}>
          Loading land intelligence…
        </div>
      ) : !data ? null : (
        <>
          {/* MAP TAB */}
          {activeTab === 'map' && (
            <LandValueMap
              values={data.values || []}
              height="560px"
            />
          )}

          {/* BREAKDOWN TAB */}
          {activeTab === 'breakdown' && (
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              {/* By category */}
              <div style={{ flex: '1 1 300px', background: '#1e293b', borderRadius: 10,
                padding: 20, minWidth: 260 }}>
                <div style={{ fontSize: 13, color: '#94a3b8', fontWeight: 700,
                  marginBottom: 14, letterSpacing: 1 }}>
                  BY CATEGORY
                </div>
                {data.byCategory?.length === 0 && (
                  <div style={{ color: '#475569', fontSize: 13 }}>No data</div>
                )}
                {data.byCategory?.map(s => (
                  <DistBar
                    key={s._id}
                    label={CATEGORY_LABEL[s._id] || s._id}
                    icon={CATEGORY_ICON[s._id]}
                    count={s.count}
                    max={maxCat}
                    color={CATEGORY_COLOR[s._id] || '#94a3b8'}
                  />
                ))}
              </div>

              {/* By community */}
              <div style={{ flex: '1 1 300px', background: '#1e293b', borderRadius: 10,
                padding: 20, minWidth: 260 }}>
                <div style={{ fontSize: 13, color: '#94a3b8', fontWeight: 700,
                  marginBottom: 14, letterSpacing: 1 }}>
                  BY COMMUNITY
                </div>
                {data.byTenant?.length === 0 && (
                  <div style={{ color: '#475569', fontSize: 13 }}>No data</div>
                )}
                {data.byTenant?.map(s => (
                  <div key={s._id} style={{ marginBottom: 10 }}>
                    <DistBar
                      label={s.tenant?.name || 'Unknown'}
                      count={s.count}
                      max={maxTen}
                      color="#0ea5e9"
                    />
                    <div style={{ fontSize: 11, color: '#475569', marginTop: -4 }}>
                      {s.verified} verified of {s.count}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* RECENT TAB */}
          {activeTab === 'recent' && (
            <div style={{ background: '#1e293b', borderRadius: 10, padding: 20 }}>
              <div style={{ fontSize: 13, color: '#94a3b8', fontWeight: 700,
                marginBottom: 14, letterSpacing: 1 }}>
                RECENT SUBMISSIONS
              </div>
              {data.recent?.length === 0 && (
                <div style={{ color: '#475569', fontSize: 13 }}>No recent values</div>
              )}
              {data.recent?.map(v => <RecentRow key={v._id} v={v} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
